import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Footprints, Save, Play } from "lucide-react";

const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");

export default function RouteSequencing() {
    const [routes, setRoutes] = useState([]);
    const [routeId, setRouteId] = useState("");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/routes").then(({ data }) => { setRoutes(data); if (data[0]) setRouteId(data[0].id); }); }, []);
    const load = () => routeId && api.get(`/sequencing/routes/${routeId}`).then(({ data }) => setData(data)).catch(err);
    useEffect(() => { load(); }, [routeId]); // eslint-disable-line react-hooks/exhaustive-deps

    const act = async (fn, msg) => {
        setBusy(true);
        try { await fn(); toast.success(msg); load(); } catch (e) { err(e); } finally { setBusy(false); }
    };
    const capture = () => act(() => api.post(`/sequencing/routes/${routeId}/capture`), "Today's driver order recorded");
    const save = (apply) => act(() => api.post(`/sequencing/routes/${routeId}/save-template`, { apply_now: apply }),
        apply ? "Template saved and applied to the route" : "Saved as a route template");

    return (
        <div data-testid="sequencing-page">
            <PageHeader title="Driver Route Sequencing"
                        subtitle="Learns the order drivers actually service stops and turns it into future route templates" />
            <div className="px-8 py-6 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                    <Select value={routeId} onValueChange={setRouteId}>
                        <SelectTrigger className="w-72" data-testid="sequencing-route-select"><SelectValue placeholder="Pick a route" /></SelectTrigger>
                        <SelectContent>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.day}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={capture} disabled={busy} data-testid="sequencing-capture-btn">
                        <Footprints className="w-4 h-4 mr-1" />Record today's run
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => save(false)} disabled={busy || !data?.runs_used} data-testid="sequencing-save-btn">
                        <Save className="w-4 h-4 mr-1" />Save as template
                    </Button>
                    <Button size="sm" onClick={() => save(true)} disabled={busy || !data?.runs_used} data-testid="sequencing-apply-btn">
                        <Play className="w-4 h-4 mr-1" />Save & apply to route
                    </Button>
                </div>
                {data && (
                    <div className="text-sm text-muted-foreground" data-testid="sequencing-summary">
                        {data.runs_used
                            ? <>Learned from <b className="text-foreground">{data.runs_used}</b> driver run{data.runs_used === 1 ? "" : "s"} ({data.run_dates.join(", ")}). Current route matches the driver order at <b className="text-foreground">{data.agreement_pct}%</b> of stops.</>
                            : "No driver runs recorded yet. Runs are captured automatically when a route is completed, or click \u201cRecord today's run\u201d."}
                    </div>
                )}
                {data && (
                    <div className="grid md:grid-cols-2 gap-4">
                        <OrderList title="Current route order" items={data.current} testid="sequencing-current" />
                        <OrderList title="Driver's learned order" items={data.learned} testid="sequencing-learned" highlight={data.current} />
                    </div>
                )}
            </div>
        </div>
    );
}

function OrderList({ title, items, testid, highlight }) {
    return (
        <Card><CardContent className="p-4">
            <div className="font-heading font-semibold mb-3">{title}</div>
            {!items.length && <div className="text-xs text-muted-foreground">Nothing yet</div>}
            <ol className="space-y-1" data-testid={testid}>
                {items.map((s, i) => {
                    const moved = highlight && highlight[i]?.id !== s.id;
                    return (
                        <li key={s.id} className={`flex gap-3 text-sm px-2 py-1 rounded ${moved ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}>
                            <span className="w-6 text-right tabular text-muted-foreground">{i + 1}</span>
                            <span className="font-medium">{s.name}</span>
                            <span className="text-xs text-muted-foreground truncate">{s.address}</span>
                        </li>
                    );
                })}
            </ol>
        </CardContent></Card>
    );
}
